import React, { useState } from "react";
import { useSimularInstruccionMutation } from "../../libs/redux/slices/Simulab/simulabApiSlice";
import { Cabepaso, Salidasm } from "./Pasolevx";
import { ejecutar } from "./simulab";

/** Datos de ejemplo con que arranca el formulario */
const EJEMPLO = { PLACAXXX: "WPK482", CONDNOMX: "PEDRO PEREZ", CONDIDXX: "1020304050", CONDCELX: "3001234567", ESCOLTAX: "NO", TRANIDXX: "" };
/** Campos que se pueden «olvidar» para probar la instrucción incompleta */
const FALTANTES = [
  { id: "", texto: "No, llega completa" },
  { id: "PLACAXXX", texto: "Falta la placa" },
  { id: "CONDNOMX", texto: "Falta el conductor" },
  { id: "CONDIDXX", texto: "Falta la cédula" },
  { id: "CONDCELX", texto: "Falta el celular" },
];

/**
 * Paso 2 del simulador: responden el correo que pide la instrucción de entrega. El cliente (canal CORREO_CLIENTE) o el
 * Ejecutivo (CORREO_EJECUTIVO) mandan placa, conductor, cédula, celular y escolta; Customs AI los lee y los entrega a
 * CIMA. «Falta un dato» deja ese campo sin leer para ver la instrucción incompleta.
 * @param {object} props
 * @param {string} props.docnroxx DO
 * @param {object} [props.foto] «Qué pasó» (transportadores entre los que hay que elegir)
 * @param {Function} props.alTerminar Refresca el «Qué pasó»
 * @author Claude Code (Alexandra Parra) <direccionit.siaco@repremundo.com.co>
 * @since 2026-10-26
 */
const Respinst = ({ docnroxx, foto, alTerminar }) => {
  const [simular, { isLoading }] = useSimularInstruccionMutation();
  const [datos, setDatos] = useState(EJEMPLO);
  const [falta, setFalta] = useState("");
  const [yaActa, setYaActa] = useState(true);
  const [salida, setSalida] = useState(null);
  const cambiar = (campo) => (e) => setDatos({ ...datos, [campo]: e.target.value });
  /**
   * Manda la respuesta por el canal de quien responde.
   * @param {string} canal CORREO_CLIENTE | CORREO_EJECUTIVO
   */
  const responder = (canal) =>
    ejecutar(simular, armarCuerpo(docnroxx, datos, falta, canal, yaActa), resumir, (r) => {
      setSalida(r);
      alTerminar();
    });
  return (
    <div className="rio-card sim-paso rio-form">
      <Cabepaso numero="2" titulo="Responden con la placa y el conductor" quien="Lo hace el cliente (o el Ejecutivo) por correo; Customs AI lo lee" />
      <Camposin datos={datos} cambiar={cambiar} entregables={foto?.INSTRUCCION?.ENTREGAB ?? []} falta={falta} setFalta={setFalta} />
      <label className="sim-opcion">
        <input type="checkbox" checked={yaActa} onChange={(e) => setYaActa(e.target.checked)} /> Emitir el acta ya, sin esperar la cola
      </label>
      <div className="sim-ejemplo">{textoLibre(datos, falta)}</div>
      <div className="sim-botones">
        <button type="button" className="sim-boton principal" disabled={isLoading} onClick={() => responder("CORREO_CLIENTE")}>Responde el cliente</button>
        <button type="button" className="sim-boton" disabled={isLoading} onClick={() => responder("CORREO_EJECUTIVO")}>Responde el Ejecutivo</button>
      </div>
      <Salidasm salida={salida} />
    </div>
  );
};

/**
 * Cuerpo para el simulador de Customs AI: el campo «olvidado» no viaja y queda como no leído.
 * @param {string} docnroxx DO
 * @param {object} datos Campos del formulario
 * @param {string} falta Campo que no se leyó ('' = ninguno)
 * @param {string} canal Canal de quien responde
 * @param {boolean} yaActa Emitir el acta sin esperar la cola
 * @returns {object} Cuerpo del POST
 */
const armarCuerpo = (docnroxx, datos, falta, canal, yaActa) => {
  const campos = Object.fromEntries(Object.entries(datos).filter(([campo, valor]) => campo !== falta && `${valor}`.trim() !== ""));
  return { DOCNROXX: docnroxx, CANALXXX: canal, SINCOLAX: yaActa ? "SI" : "NO", ...campos, ...(falta ? { NOLEIDOS: [falta] } : {}) };
};

/**
 * Lo que respondió CIMA, en una frase.
 * @param {object} d Estado de la instrucción
 * @returns {string}
 */
const resumir = (d) => {
  const avisos = (d.AVISOS ?? []).length ? `\nNo leído: ${d.AVISOS.join(", ")}` : "";
  return (d.COMPLETA ? "Instrucción COMPLETA: la entrega se desbloquea." : `Instrucción guardada, pero falta: ${(d.FALTAN ?? []).join(", ") || "—"}.`) + avisos;
};

/**
 * Ejemplo del correo en texto libre que habría escrito quien responde (Customs AI lo convierte en los campos).
 * @param {object} datos Campos
 * @param {string} falta Campo que no viene
 * @returns {string}
 */
const textoLibre = (datos, falta) => {
  const linea = (campo, texto) => (campo === falta ? "" : texto);
  return [
    "Ejemplo del correo: «Buenos días, para el retiro de la carga:",
    linea("PLACAXXX", `placa ${datos.PLACAXXX},`),
    linea("CONDNOMX", `conductor ${datos.CONDNOMX},`),
    linea("CONDIDXX", `C.C. ${datos.CONDIDXX},`),
    linea("CONDCELX", `celular ${datos.CONDCELX}.`),
    `${datos.ESCOLTAX === "SI" ? "Va con escolta." : "Sin escolta."} Gracias.»`,
  ].filter(Boolean).join(" ");
};

/**
 * Campos del formulario corto.
 * @param {object} props
 * @param {object} props.datos Valores
 * @param {Function} props.cambiar Cambio por campo
 * @param {Array<{ID: string, NOMBRE: string}>} props.entregables Transportadores entre los que elegir
 * @param {string} props.falta Campo «olvidado»
 * @param {Function} props.setFalta Cambia el campo «olvidado»
 * @returns {JSX.Element}
 */
const Camposin = ({ datos, cambiar, entregables, falta, setFalta }) => (
  <div className="rio-form-grid dos">
    <label className="rio-field">Placa<input value={datos.PLACAXXX} onChange={cambiar("PLACAXXX")} /></label>
    <label className="rio-field">Conductor<input value={datos.CONDNOMX} onChange={cambiar("CONDNOMX")} /></label>
    <label className="rio-field">Cédula<input value={datos.CONDIDXX} onChange={cambiar("CONDIDXX")} /></label>
    <label className="rio-field">Celular<input value={datos.CONDCELX} onChange={cambiar("CONDCELX")} /></label>
    <label className="rio-field">Escolta
      <select value={datos.ESCOLTAX} onChange={cambiar("ESCOLTAX")}><option value="NO">No</option><option value="SI">Sí</option></select>
    </label>
    <label className="rio-field">¿Falta un dato?
      <select value={falta} onChange={(e) => setFalta(e.target.value)}>{FALTANTES.map((m) => <option key={m.id} value={m.id}>{m.texto}</option>)}</select>
    </label>
    {entregables.length > 1 ? (
      <label className="rio-field">Transportador (el cliente tiene varios)
        <select value={datos.TRANIDXX} onChange={cambiar("TRANIDXX")}>
          <option value="">Sin decir cuál</option>
          {entregables.map((m) => <option key={m.ID} value={m.ID}>{m.NOMBRE}</option>)}
        </select>
      </label>
    ) : null}
  </div>
);

export default Respinst;
